/**
 * The reliable transport's stream, for the browser's end of a terminal
 * session (docs/GatewayReliableTransport.md).
 *
 * A copy of `gateway_api_proto::reliable::Stream`, with the same rules: the
 * path between the browser and its gateway can lose, duplicate and reorder
 * messages across the gateway's stream rolls, so each direction is numbered.
 * The sender keeps what isn't acknowledged within a byte bound and re-sends it;
 * the receiver drops duplicates, holds early arrivals and releases them in
 * order, and acknowledges what the caller has taken, which is the flow
 * control. Only the browser's part is here: it never stamps a run of its own,
 * and it never changes gRPC stream.
 *
 * No I/O and no clock: the caller passes `now` in milliseconds, sends what
 * `pollTransmit` returns, hands arrivals to `receive`, takes messages with
 * `pollReceived`, and calls `handleTimeout` at `pollTimeout`.
 */

/** The transport's part of a frame, as the backend relays it. */
export interface StreamHeader {
  streamId: string;
  /** This direction's number for the frame, from 1. 0: an acknowledgement only. */
  seq: number;
  /** The other direction: every frame through this number has been delivered. */
  ack: number;
  /** The last frame in this direction. */
  close: boolean;
  /** The receiver saw a gap: re-send what's after `ack`, sparing what's recent. */
  resend: boolean;
  /** The gateway's run. A new one means it restarted and kept nothing. */
  run: string;
  /** The gateway is on a new gRPC stream: re-send everything after `ack`. */
  pathChanged: boolean;
}

export interface Settings {
  ackDelayMs: number;
  resendAfterMs: number;
  maxResendAfterMs: number;
  resendGuardMs: number;
  windowBytes: number;
  giveUpAfterMs: number;
}

/** The doc's starting values, as the Rust core's defaults. */
export const DEFAULT_SETTINGS: Settings = {
  ackDelayMs: 50,
  resendAfterMs: 500,
  maxResendAfterMs: 4_000,
  resendGuardMs: 100,
  windowBytes: 1024 * 1024,
  giveUpAfterMs: 60_000,
};

/** What a frame costs against a bound beyond its message. */
const FRAME_OVERHEAD = 32;

export type Received<T> = { kind: 'message'; message: T } | { kind: 'closed' };

/** Why a stream is over. */
export type Ended = 'finished' | 'gaveUp' | 'reset';

export type SendResult = 'ok' | 'full' | 'closed';

interface Outgoing<T> {
  seq: number;
  message: T | undefined;
  close: boolean;
  cost: number;
  sentAt: number | undefined;
  due: boolean;
}

interface Incoming<T> {
  message: T | undefined;
  close: boolean;
  cost: number;
}

export class ReliableStream<Out, In> {
  private peerRun: string | undefined;
  private endedAs: Ended | undefined;
  private heardAt: number | undefined;

  private nextSeq = 1;
  private unacked: Outgoing<Out>[] = [];
  private unackedCost = 0;
  private closeQueued = false;
  private resendAfter: number;
  private progressAt: number | undefined;

  private delivered = 0;
  private ready: Incoming<In>[] = [];
  private held = new Map<number, Incoming<In>>();
  private bufferedCost = 0;
  private peerCloseSeq: number | undefined;
  private reportClosed = false;
  private peerClosed = false;
  private ackDue: number | undefined;
  private resendPending = false;
  private resendAskedFor: number | undefined;

  constructor(
    readonly id: string,
    private readonly settings: Settings,
    /** A message's size in bytes, either way, for the bounds. */
    private readonly costOf: (message: Out | In) => number,
  ) {
    this.resendAfter = settings.resendAfterMs;
  }

  ended(): Ended | undefined {
    return this.endedAs;
  }

  lastHeard(): number | undefined {
    return this.heardAt;
  }

  finalAck(): number {
    return this.delivered;
  }

  /** Whether `send` takes another message: open, and under the bound. */
  hasRoom(): boolean {
    return (
      this.endedAs === undefined &&
      !this.closeQueued &&
      (this.unacked.length === 0 || this.unackedCost < this.settings.windowBytes)
    );
  }

  send(message: Out, now: number): SendResult {
    if (this.endedAs !== undefined || this.closeQueued) return 'closed';
    if (!this.hasRoom()) return 'full';
    this.push(message, false, this.costOf(message) + FRAME_OVERHEAD, now);
    return 'ok';
  }

  /** Close this direction after what's already queued. */
  close(now: number): void {
    if (this.endedAs !== undefined || this.closeQueued) return;
    this.closeQueued = true;
    const last = this.unacked.at(-1);
    if (last && last.sentAt === undefined) {
      last.close = true;
      return;
    }
    this.push(undefined, true, FRAME_OVERHEAD, now);
  }

  private push(message: Out | undefined, close: boolean, cost: number, now: number): void {
    if (this.unacked.length === 0) this.progressAt = now;
    this.unacked.push({ seq: this.nextSeq, message, close, cost, sentAt: undefined, due: true });
    this.nextSeq += 1;
    this.unackedCost += cost;
  }

  /** A frame for this stream arrived. */
  receive(header: StreamHeader, message: In | undefined, now: number): void {
    if (this.endedAs === 'finished') {
      // The other end may not have heard the last acknowledgement.
      if (header.seq > 0) this.ackDue = now;
      return;
    }
    if (this.endedAs !== undefined) return;
    if (!this.acceptRun(header.run)) return;
    this.heardAt = now;
    this.takeAck(header.ack, now);
    if (header.pathChanged) {
      this.markResend(now, 0);
    } else if (header.resend) {
      this.markResend(now, this.settings.resendGuardMs);
    }
    if (header.seq > 0) this.takeFrame(header.seq, message, header.close, now);
    this.checkFinished();
  }

  private acceptRun(run: string): boolean {
    if (run === '') return true;
    if (this.peerRun === undefined) {
      this.peerRun = run;
      return true;
    }
    if (this.peerRun === run) return true;
    this.endedAs = 'reset';
    return false;
  }

  private takeAck(ack: number, now: number): void {
    let advanced = false;
    for (;;) {
      const front = this.unacked.at(0);
      // A frame that never went out can't have been delivered.
      if (!front || front.seq > ack || front.sentAt === undefined) break;
      this.unackedCost -= front.cost;
      this.unacked.shift();
      advanced = true;
    }
    if (advanced) {
      this.resendAfter = this.settings.resendAfterMs;
      this.progressAt = this.unacked.length === 0 ? undefined : now;
    }
  }

  private markResend(now: number, guard: number): void {
    for (const frame of this.unacked) {
      if (frame.sentAt !== undefined && now - frame.sentAt >= guard) frame.due = true;
    }
  }

  private receivedThrough(): number {
    return this.delivered + this.ready.length;
  }

  private takeFrame(seq: number, message: In | undefined, close: boolean, now: number): void {
    const receivedThrough = this.receivedThrough();
    if (seq <= receivedThrough || this.held.has(seq)) {
      // A duplicate: the acknowledgement for it may have been lost.
      this.ackDue = now;
      return;
    }
    if (this.peerCloseSeq !== undefined && seq > this.peerCloseSeq) return;
    const cost = (message === undefined ? 0 : this.costOf(message)) + FRAME_OVERHEAD;
    // The frame that's next is taken up to twice the bound, because early ones
    // may fill it by themselves; an early one is dropped at the bound.
    const next = seq === receivedThrough + 1;
    const limit = next ? 2 * this.settings.windowBytes : this.settings.windowBytes;
    if (this.bufferedCost > 0 && this.bufferedCost >= limit) return;
    this.bufferedCost += cost;
    if (close) this.peerCloseSeq = seq;
    const incoming: Incoming<In> = { message, close, cost };
    if (next) {
      this.ready.push(incoming);
      for (;;) {
        const following = this.held.get(this.receivedThrough() + 1);
        if (!following) break;
        this.held.delete(this.receivedThrough() + 1);
        this.ready.push(following);
      }
      if (this.held.size === 0) this.resendAskedFor = undefined;
    } else {
      this.held.set(seq, incoming);
      // A gap: ask for what's missing, once per gap.
      if (this.resendAskedFor !== receivedThrough) {
        this.resendAskedFor = receivedThrough;
        this.resendPending = true;
        this.ackDue = now;
      }
    }
  }

  /** The next message, in the other end's order. Taking it lets the acknowledgement move on. */
  pollReceived(now: number): Received<In> | undefined {
    for (;;) {
      if (this.reportClosed) {
        this.reportClosed = false;
        this.peerClosed = true;
        this.checkFinished();
        return { kind: 'closed' };
      }
      if (this.endedAs === 'gaveUp' || this.endedAs === 'reset') return undefined;
      const incoming = this.ready.shift();
      if (!incoming) return undefined;
      this.delivered += 1;
      this.bufferedCost -= incoming.cost;
      if (incoming.close) {
        this.ackDue = now;
        this.reportClosed = true;
      } else if (this.ackDue === undefined) {
        this.ackDue = now + this.settings.ackDelayMs;
      }
      if (incoming.message !== undefined) return { kind: 'message', message: incoming.message };
    }
  }

  /** The next frame to put on the wire, or undefined. Call until undefined. */
  pollTransmit(now: number): { header: StreamHeader; message: Out | undefined } | undefined {
    if (this.endedAs === 'gaveUp' || this.endedAs === 'reset') return undefined;
    const run = this.peerRun ?? '';
    const ack = this.delivered;
    const frame = this.unacked.find((f) => f.due);
    if (frame) {
      frame.due = false;
      frame.sentAt = now;
      this.ackDue = undefined;
      const resend = this.resendPending;
      this.resendPending = false;
      return {
        header: {
          streamId: this.id,
          seq: frame.seq,
          ack,
          close: frame.close,
          resend,
          run,
          pathChanged: false,
        },
        message: frame.message,
      };
    }
    if (this.ackDue !== undefined && this.ackDue <= now) {
      this.ackDue = undefined;
      const resend = this.resendPending;
      this.resendPending = false;
      return {
        header: { streamId: this.id, seq: 0, ack, close: false, resend, run, pathChanged: false },
        message: undefined,
      };
    }
    return undefined;
  }

  /** When `handleTimeout` next has something to do. */
  pollTimeout(): number | undefined {
    if (this.endedAs === 'gaveUp' || this.endedAs === 'reset') return undefined;
    let next = this.ackDue;
    const consider = (at: number) => {
      next = next === undefined ? at : Math.min(next, at);
    };
    if (this.endedAs === undefined) {
      if (this.progressAt !== undefined) consider(this.progressAt + this.settings.giveUpAfterMs);
      let oldest: number | undefined;
      for (const frame of this.unacked) {
        if (!frame.due && frame.sentAt !== undefined) {
          oldest = oldest === undefined ? frame.sentAt : Math.min(oldest, frame.sentAt);
        }
      }
      if (oldest !== undefined) consider(oldest + this.resendAfter);
    }
    return next;
  }

  /** Re-send what has waited too long, or give up. */
  handleTimeout(now: number): void {
    if (this.endedAs !== undefined) return;
    if (this.progressAt !== undefined && now >= this.progressAt + this.settings.giveUpAfterMs) {
      this.endedAs = 'gaveUp';
      return;
    }
    let resent = false;
    for (const frame of this.unacked) {
      if (!frame.due && frame.sentAt !== undefined && now - frame.sentAt >= this.resendAfter) {
        frame.due = true;
        resent = true;
      }
    }
    if (resent) this.resendAfter = Math.min(this.resendAfter * 2, this.settings.maxResendAfterMs);
  }

  private checkFinished(): void {
    if (this.endedAs === undefined && this.closeQueued && this.unacked.length === 0 && this.peerClosed) {
      this.endedAs = 'finished';
    }
  }
}
